/**
 * Admin card to run Global-E to ReturnGo Shopify portal migration; warns when a prior completed migration exists for the selected store.
 */
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import ListLayout from "../../../components/layouts/list-layout/list-layout";
import { Button } from "../../../components/inputs/button/button";
import GlobaleEnvironmentSelector from "./components/globale-environment-selector/globale-environment-selector";
import Card from "../../../components/layouts/card/card";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import ShopsSelector from "../../../components/shops-selector/shops-selector";
import { FormFieldLine } from "../../../components/form-field-line/form-field-line";
import migrateGlobaleShopifyPlatformStore from "./stores/migrate-globale-shopify-platform.store";
import supportedGlobalEEnvsStore from "./stores/supported-global-e-envs-store";
import { Input } from "../../../components/inputs/input/input";
import { isSet } from "../../../models/model-types-methods";
import { Note, NOTE_TYPE } from "../../../components/note/note";
import { ConfirmModal } from "../../../components/modal/components/confirm-modal/confirm-modal";
import { BUTTON_VARIANTS } from "../../../components/inputs/button-variants";

function formatMigrationRunDate(isoDate: string | null): string {
    if (!isoDate) {
        return "a previous migration run";
    }
    return new Date(isoDate).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

const MigrateGlobaleShopifyPlatformCard: React.FC = observer(() => {
    const store = migrateGlobaleShopifyPlatformStore;
    const areEnvironmentsLoading = supportedGlobalEEnvsStore.isLoading || !supportedGlobalEEnvsStore.isLoaded;
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // HT-710: the Shopify migration deletes and recreates migration entities (policies, return
    // methods, ...) and cannot be rolled back automatically. Require an explicit confirmation
    // before firing the API call.
    const handleMigrateClick = () => {
        if (!store.isValid || store.isProcessing) {
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirmMigrate = () => {
        setShowConfirmModal(false);
        void store.submit();
    };

    const selectedPortal = store.portal[0];

    return (

        <Card isExpanded={true}
            toolbar={{title: 
                <>
                    <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>Migrate Shopify Platform Global-E Store </FontBody1> 
                    <FontBody1 color={FONT_COLOR.PRIMARY2}>(backend/shopify)</FontBody1>
                </>
            }}  
        >
            <ListLayout>
                <FormFieldLine label="Select Shopify portal to migrate">
                    <ShopsSelector value={store.portal} onChange={store.setPortal} disabled={store.isProcessing || isSet(store.portal[0])} includeSecondaryPortals />
                </FormFieldLine>

                {isSet(store.portal[0]) && isSet(store.lastCompletedMigrationAttempt) ? (
                    <Note
                        type={NOTE_TYPE.WARNING}
                        caption="PAY ATTENTION"
                        body={
                            <>
                                This store was migrated in{" "}
                                {formatMigrationRunDate(store.lastCompletedMigrationAttempt.migration_finished_at)}. Rerunning the migration will delete
                                migration entities such as policies and return methods and recreate them. It is not advised to rerun the migration for a fully
                                live merchant.
                            </>
                        }
                    />
                ) : null}

                <FormFieldLine label="Global-E Merchant ID">
                    <Input value={store.gobaleMerchantId} required disabled />
                </FormFieldLine>

                <GlobaleEnvironmentSelector value={store.env} disbled />

                <Button
                    onClick={handleMigrateClick}
                    text="Start migration"
                    hasLoader={true}
                    isLoading={store.isProcessing}
                    disabled={areEnvironmentsLoading || store.isProcessing || !store.isValid}
                />

            </ListLayout>

            {showConfirmModal && isSet(selectedPortal) && (
                <ConfirmModal
                    title="Migrate Shopify Platform Global-E Store"
                    text=""
                    implicationText={
                        <>
                            You are about to run the Shopify Global-E migration for portal <strong>"{selectedPortal.name ?? selectedPortal.id}"</strong> with Global-E merchant ID <strong>{store.gobaleMerchantId}</strong> on the <strong>{store.env}</strong> environment. <mark><em>This action is irreversible</em></mark> — existing migration entities (policies, return methods, ...) will be deleted and recreated.
                        </>
                    }
                    questionText="Confirm?"
                    cancelButton={{
                        title: "Cancel",
                        onClick: () => setShowConfirmModal(false),
                    }}
                    okButton={{
                        title: "Confirm",
                        onClick: handleConfirmMigrate,
                        variant: BUTTON_VARIANTS.PrimaryAccent,
                    }}
                />
            )}
        </Card>

    );
});

export default MigrateGlobaleShopifyPlatformCard;
